import produce from 'immer';
import createCachedSelector from 're-reselect';
import { getType } from 'typesafe-actions';
import { actions } from '..';
import { Task } from '../../model';
import { Actions, StoreState } from '../types';

interface TasksById {
  [contactId: string]: Task[]
}

export default function emails(state: TasksById = {}, action: Actions): TasksById {
  switch (action.type) {

    case getType(actions.deleteContact): {
      const { contact } = action.payload
      const { [contact.id]: _, ...rest } = state
      return rest
    }

    case getType(actions.fetchMediaTasksResponse): {
      const { contactId, tasks } = action.payload
      return {
        ...state,
        [contactId]: tasks,
      }
    }

    case getType(actions.fetchMediaTaskItemsResponse): {
      return produce(state, state => {
        const { mediaId, contactId, taskId, items } = action.payload

        const tasks = state[contactId === '' ? mediaId : contactId]
        if (!tasks) return

        const task = tasks.find(t => t.taskId === taskId)
        if (!task) return

        task.items = items
      })
    }

    case getType(actions.createNewTaskResponse): {
      return produce(state, state => {
        const { contactId, task } = action.payload

        const tasks = state[contactId]
        if (!tasks) return

        tasks.splice(1, 0, task)
      })
    }

    case getType(actions.deleteMediaTaskAction): {
      return produce(state, state => {
        const { contactId, taskId } = action.payload

        const tasks = state[contactId]
        if (!tasks) return

        const index = tasks.findIndex(t => t.taskId === taskId)
        if (index !== -1) {
          tasks.splice(index, 1)
        }
      })
    }

    case getType(actions.updateMediaTaskSubjectRequest):
      return produce(state, state => {
        const { contactId, taskId, subject } = action.payload

        const tasks = state[contactId]
        if (!tasks) return

        const task = tasks.find(t => t.taskId === taskId)
        if (!task) return

        task.subject = subject
      })

    case getType(actions.updateMediaTaskStateRequest):
      return produce(state, state => {
        const { contactId, taskId, stateId } = action.payload

        const tasks = state[contactId]
        if (!tasks) return

        const task = tasks.find(t => t.taskId === taskId)
        if (!task) return

        task.stateId = stateId
      })

    case getType(actions.addMediaItemTask): {
      return produce(state, state => {
        const { contactId, taskId, item } = action.payload

        const tasks = state[contactId]
        if (!tasks) return

        const task = tasks.find(t => t.taskId === taskId)
        if (!task) return

        if (!task.items) return
        //only add if there are results as that means we have asked for the data already

        task.items.push(item)
        task.items.sort((a, b) => a.dateTimeUtc && b.dateTimeUtc && a.dateTimeUtc < b.dateTimeUtc ? 1 : -1)
      })
    }

    case getType(actions.removeMediaItemTask): {
      return produce(state, state => {
        const { contactId, taskId, itemId } = action.payload

        const tasks = state[contactId]
        if (!tasks) return

        const task = tasks.find(t => t.taskId === taskId)
        if (!task) return

        if (!task.items) return

        const index = task.items.findIndex(i => i.itemId === itemId)
        task.items.splice(index, 1)
      })
    }

    case getType(actions.updateMediaItemTaskSubject): {
      return produce(state, state => {
        const { contactId, taskId, itemId, subject } = action.payload

        const tasks = state[contactId]
        if (!tasks) return

        const task = tasks.find(t => t.taskId === taskId)
        if (!task) return
        if (!task.items) return

        const item = task.items.find(i => i.itemId === itemId)

        if (!item) return

        item.subject = subject
      })
    }
  }

  return state
}

function getRoot(state: StoreState) { return state.tasks }

export function selectTasks(state: StoreState, mediaId: string, contactId: string) {
  const tasks = getRoot(state)[contactId === '' ? mediaId : contactId]
  return (tasks) ? tasks : undefined
}

interface SelectTaskArgs {
  mediaId: string
  contactId: string
  taskId: string
}

export const selectTaskItems = createCachedSelector(
  getRoot,
  (s: StoreState, { mediaId }: SelectTaskArgs) => mediaId,
  (s: StoreState, { contactId }: SelectTaskArgs) => contactId,
  (s: StoreState, { taskId }: SelectTaskArgs) => taskId,
  (state, mediaId, contactId, taskId) => {
    const tasks = state[contactId === '' ? mediaId : contactId]
    if (!tasks) { return [] }
    const task = tasks.find(t => t.taskId === taskId)
    return task ? task.items : []
  },
)(
  (s, { mediaId, contactId, taskId }) => `${contactId === '' ? mediaId : contactId}-${taskId}`,
)

export const selectTaskByTaskId = createCachedSelector(
  getRoot,
  (s: StoreState, taskId: string) => taskId,
  (state, taskId) => {
    for (const tasks of Object.values(state)) {
      for (const task of tasks) {
        if (task && task.taskId === taskId) {
          return task
        }
      }
    }
  },
)(
  (s, taskId) => taskId,
)

export const selectTaskByItemId = createCachedSelector(
  getRoot,
  (s: StoreState, itemId: string) => itemId,
  (state, itemId) => {
    for (const tasks of Object.values(state)) {
      for (const task of tasks) {
        if (task && task.items) {
          if (task.items.find(i => i.itemId === itemId)) {
            return task
          }
        }
      }
    }
  },
)(
  (s, itemId) => itemId,
)

export const selectItemByItemId = createCachedSelector(
  getRoot,
  (s: StoreState, itemId: string) => itemId,
  (state, itemId) => {
    for (const tasks of Object.values(state)) {
      for (const task of tasks) {
        if (task && task.items) {
          const item = task.items.find(i => i.itemId === itemId)
          if (item) {
            return item
          }
        }
      }
    }
  },
)(
  (s, itemId) => itemId,
)
